import React, { Fragment, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react";
import { MdPersonAdd, MdKeyboardVoice, MdPhotoCamera, MdVideocam, MdNotificationsNone, MdAdd } from "react-icons/md";
import { BsFillFileTextFill } from "react-icons/bs";
import { HiMenuAlt2 } from "react-icons/hi";

import CreatePost from "./CreatePost";
import LeftSidebar_mobile from "./LeftSidebar_mobile";
import UserSearch from "./UserSearch";
import NotificationPostModal from "./NotificationPostModal";

import { getMediaUrl } from "../lib/utils";
import { getNotifications, readNotifications } from "../api";

const createOptions = [
  { icon: <BsFillFileTextFill size={19} />, title: "Text", description: "Share what is on your mind", bg: "bg-purple-50", color: "text-purple-600", type: "text" },
  { icon: <MdKeyboardVoice size={21} />, title: "Voice", description: "Record and share your voice", bg: "bg-orange-50", color: "text-orange-500", type: "voice" },
  { icon: <MdPhotoCamera size={20} />, title: "Photo", description: "Share photos with everyone", bg: "bg-emerald-50", color: "text-emerald-600", type: "photo" },
  { icon: <MdVideocam size={21} />, title: "Video", description: "Upload a video or story", bg: "bg-blue-50", color: "text-blue-600", type: "video" },
];

const Navbar = ({ user }) => {
  const [searchToggle, setSearchToggle] = useState(false);
  const [toggleSidebar, setToggleSidebar] = useState(false);
  const [createPostOpen, setIsCreatePostOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [postType, setPostType] = useState("");
  const [notifs, setNotifs] = useState([]);
  const [selectedNotification, setSelectedNotification] = useState(null);

  const notificationSoundRef = useRef(null);
  const previousNotificationIdsRef = useRef(new Set());
  const initializedNotificationsRef = useRef(false);
  const notificationRef = useRef(null);

  /* ================= NOTIFICATION SOUND ================= */

  useEffect(() => {
    notificationSoundRef.current = new Audio("/assets/sounds/notification.mp3");
    notificationSoundRef.current.volume = 0.5;

    return () => {
      if (notificationSoundRef.current) {
        notificationSoundRef.current.pause();
        notificationSoundRef.current = null;
      }
    };
  }, []);

  /* ================= NOTIFICATIONS ================= */

  useEffect(() => {
    let mounted = true;

    const fetchNotifications = async () => {
      try {
        const res = await getNotifications();

        if (!mounted) return;

        const newNotifications = Array.isArray(res.data) ? res.data : [];
        const currentIds = new Set(newNotifications.map((notification) => Number(notification.id)));

        if (!initializedNotificationsRef.current) {
          previousNotificationIdsRef.current = currentIds;
          initializedNotificationsRef.current = true;
          setNotifs(newNotifications);
          return;
        }

        const receivedNewNotification = newNotifications.some((notification) => !previousNotificationIdsRef.current.has(Number(notification.id)));

        setNotifs(newNotifications);
        previousNotificationIdsRef.current = currentIds;

        if (receivedNewNotification && notificationSoundRef.current) {
          notificationSoundRef.current.currentTime = 0;
          notificationSoundRef.current.play().catch((error) => console.log("Notification sound blocked:", error));
        }
      } catch (error) {
        console.error("Failed to fetch notifications:", error);
      }
    };

    fetchNotifications();

    const interval = setInterval(fetchNotifications, 2000);

    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  /* ================= CLOSE NOTIFICATIONS OUTSIDE ================= */

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target)) setNotifOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ================= UNREAD COUNT ================= */

  const unreadNotifications = notifs.filter((notification) => !notification.seen).length;

  /* ================= POST TYPE ICON ================= */

  const getPostTypeIcon = (currentPostType) => {
    switch (currentPostType) {
      case "text":
        return <BsFillFileTextFill className="h-4 w-4 text-purple-600" />;

      case "photo":
        return <MdPhotoCamera className="h-5 w-5 text-emerald-600" />;

      case "video":
        return <MdVideocam className="h-5 w-5 text-blue-600" />;

      case "voice":
        return <MdKeyboardVoice className="h-5 w-5 text-orange-500" />;

      default:
        return <MdNotificationsNone className="h-5 w-5 text-gray-400" />;
    }
  };

  /* ================= NOTIFICATION ICON ================= */

  const getNotificationIcon = (notification) => {
    switch (notification?.type) {
      case "follow":
      case "follow_back":
        return <MdPersonAdd className="h-5 w-5 text-purple-600" />;

      default:
        return getPostTypeIcon(notification?.post_type);
    }
  };

  /* ================= ACTIVITY TEXT ================= */

  const getActivityText = (notification) => {
    if (notification?.activity) return notification.activity;

    switch (notification?.type) {
      case "like":
        return "liked your post";

      case "comment":
        return "commented on your post";

      case "comment_like":
        return "liked your comment";

      case "reply":
        return "replied to your comment";

      case "follow":
        return "started following you";

      case "follow_back":
        return "followed you back";

      case "new_post":
        return "shared a new post";

      default:
        return "interacted with you";
    }
  };

  /* ================= MARK AS READ ================= */

  const handleMarkAsRead = async (id) => {
    try {
      await readNotifications(id);
      setNotifs((prev) => prev.map((notification) => notification.id === id ? { ...notification, seen: 1 } : notification));
    } catch (err) {
      console.error("Unable to mark notification as read:", err);
    }
  };

  /* ================= NOTIFICATION CLICK ================= */

  const handleNotificationClick = async (notification) => {
    setNotifOpen(false);
    await handleMarkAsRead(notification.id);

    if (["like", "comment", "comment_like", "reply", "new_post"].includes(notification.type) && notification.post_id) {
      setSelectedNotification(notification);
    }
  };

  /* ================= PROFILE CLICK ================= */

  const handleProfileClick = async (notification) => {
    setNotifOpen(false);

    if (!notification.seen) {
      await handleMarkAsRead(notification.id);
    }
  };

  /* ================= NOTIFICATION TOGGLE ================= */

  const handleNotificationToggle = () => setNotifOpen((prev) => !prev);

  /* ================= CREATE POST ================= */

  const handleCreatePost = (type) => {
    setPostType(type);
    setIsCreatePostOpen(true);
  };

  return (
    <>
      {/* ================= NAVBAR ================= */}

      <header className="fixed left-0 right-0 top-0 z-40 w-full border-b border-gray-100 bg-white/95 backdrop-blur-xl">
        <div className="mx-auto flex h-[72px] w-full items-center gap-4 px-4 sm:px-6 lg:px-10">

          {/* ================= LEFT ================= */}

          <div className="flex shrink-0 items-center gap-3">

            {/* Mobile menu */}

            <button onClick={() => setToggleSidebar(true)} className="flex h-10 w-10 items-center justify-center rounded-xl text-gray-600 transition hover:bg-gray-100 hover:text-purple-600 lg:hidden">
              <HiMenuAlt2 size={26} />
            </button>

            {/* Logo - hidden on phones to preserve navbar space */}

            <Link to="/" className="group hidden items-center gap-2.5 rounded-xl sm:flex">
              <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-xl bg-purple-50 transition group-hover:bg-purple-100">
                <img src="/assets/images/logo.png" alt="Alakoze" className="h-8 w-8 object-contain" />
              </div>

              <span className="hidden text-xl font-extrabold tracking-tight text-gray-900 md:block">
                Alakoze<span className="text-purple-600">.</span>
              </span>
            </Link>
          </div>

          {/* ================= SEARCH ================= */}

          <div className="mx-auto hidden w-full max-w-xl flex-1 sm:block">
            <UserSearch searchToggle={searchToggle} setSearchToggle={setSearchToggle} />
          </div>

          {/* ================= RIGHT ================= */}

          <div className="ml-auto flex shrink-0 items-center gap-2">

            {/* Mobile Search */}

            <div className="sm:hidden">
              <UserSearch searchToggle={searchToggle} setSearchToggle={setSearchToggle} />
            </div>

            {/* ================= CREATE ================= */}

            <Menu as="div" className="relative">
              <MenuButton className="flex h-10 items-center justify-center gap-1.5 rounded-xl bg-purple-600 px-3 text-sm font-semibold text-white shadow-sm transition-all hover:bg-purple-700 hover:shadow-md active:scale-[0.97] sm:px-4">
                <MdAdd size={22} />
                <span className="hidden sm:inline">Create</span>
              </MenuButton>

              <Transition as={Fragment} enter="transition ease-out duration-150" enterFrom="opacity-0 translate-y-2 scale-95" enterTo="opacity-100 translate-y-0 scale-100" leave="transition ease-in duration-100" leaveFrom="opacity-100 translate-y-0 scale-100" leaveTo="opacity-0 translate-y-2 scale-95">
                <MenuItems className="absolute right-0 mt-3 w-[330px] origin-top-right overflow-hidden rounded-2xl border border-gray-100 bg-white p-2 shadow-[0_20px_60px_rgba(0,0,0,0.12)] focus:outline-none sm:w-[380px]">

                  <div className="px-3 pb-3 pt-2">
                    <h2 className="text-base font-bold text-gray-900">Create something</h2>
                    <p className="mt-0.5 text-sm text-gray-500">Share something with your community.</p>
                  </div>

                  <div className="space-y-1">
                    {createOptions.map((item) => (
                      <MenuItem key={item.type}>
                        <button onClick={() => handleCreatePost(item.type)} className="group flex w-full items-center gap-3.5 rounded-xl px-3 py-3 text-left transition hover:bg-gray-50">
                          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${item.bg} ${item.color} transition group-hover:scale-105`}>
                            {item.icon}
                          </div>

                          <div className="min-w-0">
                            <p className="font-semibold text-gray-900">{item.title}</p>
                            <p className="mt-0.5 text-sm text-gray-500">{item.description}</p>
                          </div>
                        </button>
                      </MenuItem>
                    ))}
                  </div>
                </MenuItems>
              </Transition>
            </Menu>

            {/* ================= NOTIFICATIONS ================= */}

            <div ref={notificationRef} className="relative">

              {/* Bell */}

              <button onClick={handleNotificationToggle} className={`relative flex h-10 w-10 items-center justify-center rounded-xl border transition-all ${notifOpen ? "border-purple-200 bg-purple-50 text-purple-600" : "border-gray-100 bg-gray-50 text-gray-600 hover:border-gray-200 hover:bg-gray-100"}`}>
                <MdNotificationsNone size={23} />

                {unreadNotifications > 0 && (
                  <span className="absolute -right-1.5 -top-1.5 flex h-[19px] min-w-[19px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white ring-2 ring-white">
                    {unreadNotifications > 9 ? "9+" : unreadNotifications}
                  </span>
                )}
              </button>

              {/* ================= NOTIFICATION DROPDOWN ================= */}

              {notifOpen && (
                <div className="absolute right-0 mt-3 w-[340px] overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-[0_20px_60px_rgba(0,0,0,0.12)] sm:w-[390px]">

                  {/* Header */}

                  <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                    <div>
                      <h3 className="font-bold text-gray-900">Notifications</h3>
                      {unreadNotifications > 0 && <p className="mt-0.5 text-xs text-gray-500">{unreadNotifications} unread</p>}
                    </div>

                    {unreadNotifications > 0 && <span className="rounded-full bg-purple-50 px-2.5 py-1 text-xs font-semibold text-purple-600">New</span>}
                  </div>

                  {/* Notification list */}

                  <div className="max-h-[440px] overflow-y-auto">
                    {!notifs.length ? (
                      <div className="px-6 py-12 text-center">
                        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-gray-50">
                          <MdNotificationsNone size={25} className="text-gray-400" />
                        </div>

                        <p className="mt-3 font-medium text-gray-700">No notifications yet</p>
                        <p className="mt-1 text-sm text-gray-400">We'll let you know when something happens.</p>
                      </div>
                    ) : (
                      notifs.slice(0, 5).map((notification) => (
                        <div key={notification.id} className={`group flex w-full items-center gap-3 border-b border-gray-50 px-4 py-3.5 text-left transition ${notification.seen ? "bg-white hover:bg-gray-50" : "bg-purple-50/60 hover:bg-purple-50"}`}>

                          {/* Clickable profile image */}

                          <Link to={`/profile/${notification.user_id}`} onClick={() => handleProfileClick(notification)} className="relative shrink-0">
                            <img src={getMediaUrl(notification.profile_img)} alt={`${notification.firstname} ${notification.lastname}`} className="h-11 w-11 rounded-full object-cover transition hover:ring-2 hover:ring-purple-300" />
                          </Link>

                          {/* Text */}

                          <div className="min-w-0 flex-1">
                            <p className="text-sm leading-5 text-gray-600">
                              <Link to={`/profile/${notification.user_id}`} onClick={() => handleProfileClick(notification)} className="font-semibold text-gray-900 hover:text-purple-600 hover:underline">
                                {notification.firstname} {notification.lastname}
                              </Link>{" "}

                              <button type="button" onClick={() => handleNotificationClick(notification)} className="text-left hover:text-purple-600">
                                {getActivityText(notification)}
                              </button>
                            </p>

                            <p className="mt-1 text-xs text-gray-400">{new Date(notification.created_at).toLocaleString()}</p>
                          </div>

                          {/* Notification action */}

                          <button type="button" onClick={() => handleNotificationClick(notification)} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gray-50 transition hover:bg-purple-50">
                            {getNotificationIcon(notification)}
                          </button>

                          {!notification.seen && <span className="h-2.5 w-2.5 shrink-0 rounded-full bg-purple-600" />}
                        </div>
                      ))
                    )}
                  </div>

                  {/* See all */}

                  {notifs.length > 0 && (
                    <Link to="/notifications" onClick={() => setNotifOpen(false)} className="flex w-full items-center justify-center border-t border-gray-100 px-4 py-3.5 text-sm font-semibold text-purple-600 transition hover:bg-purple-50">
                      See all
                    </Link>
                  )}
                </div>
              )}
            </div>

            {/* ================= PROFILE ================= */}

            <div className="ml-1 hidden items-center md:flex">
              <div className="relative">
                <Link to={`/profile/${user?.id}`}>
                  <img src={getMediaUrl(user?.profile_img)} alt="Profile" className="h-10 w-10 rounded-full object-cover ring-2 ring-gray-100 transition-all hover:ring-purple-300" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="h-[72px]" />

      {/* ================= MOBILE SIDEBAR ================= */}

      <LeftSidebar_mobile user={user} isOpen={toggleSidebar} setIsOpen={setToggleSidebar} />

      {/* ================= MODALS ================= */}

      <CreatePost isOpen={createPostOpen} isClose={() => setIsCreatePostOpen(false)} postType={postType} />

      <NotificationPostModal notification={selectedNotification} user={user} onClose={() => setSelectedNotification(null)} />
    </>
  );
};

export default Navbar;